import { Link, useLocation } from "react-router-dom";
import { Linkedin, Instagram, Mail, ArrowUpRight } from "lucide-react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import logoWhiteAsset from "@/assets/logo-white.webp.asset.json";
const logoWhite = logoWhiteAsset.url;
import BtnArrow from "@/components/ui/btn-arrow";
import { resetTaste } from "@/lib/visitorTaste";
import { labsLocations, practiceMarkets } from "@/data/locations";

// The page footer is intentionally minimal. It mirrors the editorial reference:
// brand + nav band on top, four utility columns in the middle, legal coda below.
// On snap-scroll pages the footer is portaled into the scroller as a final
// snap-start section so it remains reachable.

const Footer = () => {
  const location = useLocation();
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setPortalTarget(null);
    const find = () => {
      const candidates = document.querySelectorAll<HTMLElement>(
        "main, [data-scroll-container]"
      );
      for (const el of candidates) {
        const cs = getComputedStyle(el);
        const isScroller =
          (cs.overflowY === "auto" || cs.overflowY === "scroll") &&
          el.scrollHeight > el.clientHeight + 4;
        if (isScroller) {
          setPortalTarget(el);
          return;
        }
      }
      setPortalTarget(null);
    };
    const raf = requestAnimationFrame(find);
    const t = window.setTimeout(find, 300);
    window.addEventListener("resize", find);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(t);
      window.removeEventListener("resize", find);
    };
  }, [location.pathname]);

  const navLinks = [
    { to: "/labs", label: "Labs" },
    { to: "/system", label: "The codelumen System" },
    { to: "/solutions", label: "Capabilities" },
    { to: "/work", label: "Our Work" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
    { to: "/talent", label: "Talent" },
  ];

  const footerNode = (
    <footer
      className={`cl-fade-to-grey relative w-full bg-charcoal text-stone/55 px-6 md:px-10 lg:px-14 pt-12 md:pt-16 pb-6 md:pb-8 ${
        portalTarget ? "snap-start" : ""
      }`}
    >
      <div className="relative z-10 max-w-[1400px] mx-auto">
        {/* TOP BAND — brand (left) + nav (right) */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-8 md:gap-10 pb-8 md:pb-10">
          <div>
            <Link
              to="/"
              aria-label="codelumen"
              className="inline-block transition-opacity duration-300 hover:opacity-80"
            >
              <img src={logoWhite} alt="codelumen" width={176} height={28} className="h-5 w-auto" />
            </Link>
            <p className="mt-2 font-sans font-light italic text-stone/50 text-[12px] tracking-wide">
              Light Better. Live Better.
            </p>
          </div>

          <nav className="flex flex-wrap gap-x-8 gap-y-3 md:justify-end md:items-center md:pt-2">
            {navLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="font-sans text-[12px] tracking-[0.22em] uppercase text-stone/85 hover:text-white transition-colors duration-300"
              >
                {l.label}
              </Link>
            ))}
          </nav>
        </div>

        {/* MIDDLE BAND — four utility columns */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8 py-10 md:py-14">
          {/* codelumenLabs locations */}
          <div className="md:col-span-3">
            <p className="text-stone/55 font-sans text-[10px] tracking-[0.3em] uppercase mb-5">
              codelumenLabs Locations
            </p>
            <div className="space-y-2 mb-5">
              {labsLocations.map((location) => (
                <p key={location.city} className="font-sans font-light text-stone text-sm">
                  {location.city} <span className="text-stone/55">· {location.detail}</span>
                </p>
              ))}
            </div>
            <Link
              to="/labs"
              className="group inline-flex items-center gap-2 font-sans text-[11px] tracking-[0.22em] uppercase text-stone hover:text-white transition-colors duration-300"
            >
              Schedule a Labs Session
              <BtnArrow />
            </Link>
          </div>

          {/* Practice locations and markets */}
          <div className="md:col-span-4">
            <p className="text-stone/55 font-sans text-[10px] tracking-[0.3em] uppercase mb-5">
              Practice Locations &amp; Markets
            </p>
            <div className="space-y-2">
              {practiceMarkets.map((location) => (
                <p key={location.city} className="text-stone font-sans font-light text-sm leading-relaxed">
                  {location.city} <span className="text-stone/55">· {location.detail}</span>
                </p>
              ))}
            </div>
          </div>


          {/* Direct Contact */}
          <div className="md:col-span-3">
            <p className="text-stone/55 font-sans text-[10px] tracking-[0.3em] uppercase mb-5">
              Direct Contact
            </p>
            <a
              href="mailto:info@clumen.com"
              className="font-sans font-light text-stone text-sm hover:text-white transition-colors duration-300"
            >
              info@clumen.com
            </a>
          </div>

          {/* Follow */}
          <div className="md:col-span-2">
            <p className="text-stone/55 font-sans text-[10px] tracking-[0.3em] uppercase mb-5">
              Follow
            </p>
            <div className="flex items-center gap-6">
              <a
                href="https://www.linkedin.com/company/clumen/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="text-stone/65 hover:text-white transition-colors duration-300"
              >
                <Linkedin size={18} strokeWidth={1.4} />
              </a>
              <a
                href="https://www.instagram.com/codelumen"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="text-stone/65 hover:text-white transition-colors duration-300"
              >
                <Instagram size={18} strokeWidth={1.4} />
              </a>
              <a
                href="mailto:info@clumen.com"
                aria-label="Email"
                className="text-stone/65 hover:text-white transition-colors duration-300"
              >
                <Mail size={18} strokeWidth={1.4} />
              </a>
            </div>
          </div>
        </div>

        {/* LEGAL ROW */}
        <div className="pt-6 md:pt-8 flex flex-col md:flex-row items-center justify-between gap-3">
          <span className="font-sans text-[12px] tracking-[0.18em] text-stone/70">
            © {new Date().getFullYear()}. CODELUMEN, LLC. All rights reserved. All designs protected by copyright.
          </span>
          <div className="flex items-center gap-8">
            <button
              type="button"
              onClick={() => {
                resetTaste();
                if (typeof window !== "undefined") window.location.reload();
              }}
              className="font-sans text-[12px] tracking-[0.22em] uppercase text-stone/70 hover:text-white transition-colors duration-300"
            >
              Reset Preferences
            </button>
            <Link
              to="/terms"
              className="font-sans text-[12px] tracking-[0.22em] uppercase text-stone/70 hover:text-white transition-colors duration-300"
            >
              Terms
            </Link>
            <Link
              to="/privacy"
              className="font-sans text-[12px] tracking-[0.22em] uppercase text-stone/70 hover:text-white transition-colors duration-300"
            >
              Privacy
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );

  if (portalTarget) {
    return createPortal(footerNode, portalTarget);
  }
  return footerNode;
};

export default Footer;
